import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import Select from "@/components/shared/select";
import Checkbox from "@/components/shared/inputs/checkbox";
import { FieldGroup } from "../parts/FieldGroup";
import { OptionalTextarea } from "../parts/OptionalTextarea";
import { SubFieldRadioGroup } from "../parts/SubFieldRadioGroup";
import { useSimulatorConfiguration } from "../useSimulatorConfiguration";
import { useUpdateReleasePolicyGeneral } from "../useUpdateReleasePolicyGeneral";
import styles from "../AutomationConfiguration.module.scss";
import { AutomationConfigurationContentHeader } from "../parts/AutomationConfigurationContentHeader";
import { UpdateCustomTonePrompt } from "../parts/UpdateCustomTonePrompt";
import { useBrands } from "@/api/useBrands";
import { useWorkspace } from "@/routes/simulator/context/wrokspace";
import { useSimulator } from "@/v2/features/simulator/context/SimulatorWrapper";
import { toast } from "sonner";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { TenantBrand } from "@/v2/entities/brand/Brand.model";

export function TicketChannelGeneralConfiguration() {
  const { simulatorId } = useParams();
  const { loadingSimulator } = useSimulatorConfiguration(simulatorId);
  const { status } = useUpdateReleasePolicyGeneral(simulatorId);
  const { updateRelease, currentRelease, isLoadingCurrentRelease } =
    useSimulator();
  const { workspace } = useWorkspace();
  const knowledgeBrandId = useFlag("knowledgeBrandId");

  const [openToneOfVoice, setOpenToneOfVoice] = useState(true);

  const { data: brands, isLoading: loadingBrands } = useBrands();

  useEffect(() => {
    setOpenToneOfVoice(
      currentRelease?.openingLine != undefined ||
        currentRelease?.closingLine != undefined ||
        currentRelease?.toneOfVoice != undefined
    );
  }, [currentRelease]);

  const handleUpdateRelease = useCallback(
    (payload: {
      includeLink?: boolean;
      toneOfVoice?: string;
      openingLine?: string;
      closingLine?: string;
      knowledgeBrandId?: number;
    }) => {
      if (currentRelease?.id) {
        updateRelease(String(currentRelease.id), {
          simulatorId: currentRelease.simulatorId,
          ...payload,
        });
      } else {
        toast.error("Something went wrong, please try again");
      }
    },
    [currentRelease?.id, updateRelease]
  );

  if (loadingSimulator || isLoadingCurrentRelease || currentRelease == null) {
    <Flex
      justify={"center"}
      align={"center"}
      height={"100%"}
      width={"100%"}
      direction={"column"}
      gap={"2"}
    >
      <Text size="3" weight="medium" color="gray" highContrast>
        Loading General Configuration
      </Text>
      <Spinner size="3" />
    </Flex>;
  }

  if (!isLoadingCurrentRelease && !currentRelease) {
    return <Navigate to={`/automation/${simulatorId}`} />;
  }

  return (
    <Flex direction={"column"} gap={"24px"}>
      <AutomationConfigurationContentHeader title={"General"} status={status} />

      <Flex direction={"column"}>
        {knowledgeBrandId && (
          <FieldGroup
            label={"Brand Association"}
            description={
              "Choose the brand to associate with the tickets channel."
            }
          >
            <Select
              options={
                brands?.map((x: TenantBrand) => ({
                  label: x.name,
                  value: String(x.id),
                })) ?? []
              }
              value={
                currentRelease?.knowledgeBrandId
                  ? String(currentRelease.knowledgeBrandId)
                  : undefined
              }
              onChange={(newValue) =>
                handleUpdateRelease({ knowledgeBrandId: Number(newValue) })
              }
              loading={loadingBrands}
            />
          </FieldGroup>
        )}

        <FieldGroup
          label={"Helpful Links"}
          description={
            "Choose whether the AI response should include helpful links."
          }
        >
          <Checkbox
            label="Include helpful links"
            value={currentRelease?.includeLink ?? false}
            onChange={(checked) =>
              handleUpdateRelease({ includeLink: checked })
            }
          />
        </FieldGroup>

        <FieldGroup
          label={"Prioritize Brief Links"}
          description={
            "When a topic brief provides a link, hide knowledge-base/source links so Quack sends only the link from the brief."
          }
        >
          <Checkbox
            label="Hide source links when the brief has a link"
            value={currentRelease?.hideSourceUrls ?? false}
            onChange={(checked) => {
              if (currentRelease?.id) {
                updateRelease(String(currentRelease.id), {
                  simulatorId: currentRelease.simulatorId,
                  hideSourceUrls: checked,
                });
              } else {
                toast.error("Something went wrong, please try again");
              }
            }}
          />
        </FieldGroup>

        <FieldGroup
          label={"Style & Tone"}
          description={"Set the tone your AI uses to match your brand."}
        >
          <SubFieldRadioGroup
            value={openToneOfVoice ? "custom" : "context"}
            onChange={(newValue) => setOpenToneOfVoice(newValue === "custom")}
            options={[
              {
                value: "context",
                label: "Context-aware interactions",
                description:
                  "Quack AI's smart defaults, ensuring smooth and context-aware interactions.",
              },
              {
                value: "custom",
                label: "Custom",
                description: "Define a specific tone and style for your AI.",
              },
            ]}
          >
            {openToneOfVoice && (
              <>
                <FieldGroup
                  label={"Tone of Voice 📣"}
                  description={"Set the tone your AI uses to match your brand."}
                >
                  <Select
                    options={[
                      { label: "Formal", value: "formal" },
                      { label: "Casual", value: "casual" },
                    ]}
                    value={currentRelease?.toneOfVoice ?? "formal"}
                    onChange={(newValue) =>
                      handleUpdateRelease({ toneOfVoice: newValue })
                    }
                    classes={{ trigger: styles.Select }}
                  />
                </FieldGroup>
                <FieldGroup
                  label={"Always Open Chat With 👋🏻"}
                  description={"Set a standard opening line for responses."}
                >
                  <OptionalTextarea
                    value={currentRelease?.openingLine}
                    onChange={(newValue) =>
                      handleUpdateRelease({ openingLine: newValue })
                    }
                    buttonText={"Add opening style"}
                    label={"Always Open Chat With 👋🏻"}
                    description={
                      "Use quotation marks (“ ”) for exact phrasing to ensure precise wording."
                    }
                  />
                </FieldGroup>

                <FieldGroup
                  label={"Always End Chat With 👋🏻"}
                  description={"Define a consistent closing line."}
                >
                  <OptionalTextarea
                    value={currentRelease?.closingLine}
                    onChange={(newValue) =>
                      handleUpdateRelease({ closingLine: newValue })
                    }
                    buttonText={"Add closing style"}
                    label={"Always End Chat With 👋🏻"}
                    description={
                      "Use quotation marks (“ ”) for exact phrasing to ensure precise wording."
                    }
                  />
                </FieldGroup>
              </>
            )}
          </SubFieldRadioGroup>
        </FieldGroup>

        <UpdateCustomTonePrompt />
      </Flex>
    </Flex>
  );
}
